import styled from '@emotion/styled';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { CartValidation, ChannelTypes, SubmitApplicationError, Application, Dealer, UpdateDebitErrors, Details } from '../../gql/graphql';
import { redirectToApplicationDetails } from '../../helpers/url/url.helpers';
import { colors, fontFamilies } from '../../components/baseStyle/Variables';
import Button from '../../components/button/Button';
import DotLoader from '../../components/dotLoader/DotLoader';
import LongLoader from '../../components/longLoader/LongLoader';
import WallyPreApproval from '../../components/wally_pre_approval';
import { selectLeaseHelper } from '../../helpers';
import { Role } from '../../types/role';
import { useSessionProperies } from '../../hooks/dtrum';
import { SegmentProps } from '../../state-machine/actions/segment';
import ErrorsFound from '../errorsFound/ErrorsFound';
import { usePageText } from './pageText';
import CartValidationErrContent from '../../components/approvalContent/CartValidationErrContent';
import { getEcommApprovalOnly, getLowestMinimumSpend } from '../../helpers/leases/leases.helpers';
import { ExitCodes, ModalMessage, ModalMessageFailure, modalResolver } from '../../helpers/modal/modal.helper';
import { Trans, useTranslation } from 'react-i18next';
import { ApplicationFinancingType } from '../../constants/application-financing-type';

export type PreApprovalProps = SegmentProps & {
  onNext: (arg: { type: string; data?: any }) => void;
  isLoading: boolean;
  orderId: string;
  application: Application | undefined;
  dealer: Dealer;
  orderDetails: Details;
  authorizeCardErrors?: UpdateDebitErrors[];
  role: Role;
  onCompleteAppLater: () => void;
  applyErrors?: SubmitApplicationError[];
};

const ApprovalContainer = styled.div`
  text-align: left;

  strong {
    font-family: ${fontFamilies.interSemiBold};
    font-weight: 900;
  }
`;

const Gradient = styled.div`
  position: absolute;
  left: -2rem;
  right: -2rem;
  width: calc(100% + 4rem);
  max-width: 465px;
  margin: 0 auto;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.06) 0%, rgba(0, 0, 0, 0) 100%);
  margin-top: 1.5rem;
  height: 50px;
`;

const BottomContent = styled.div`
  margin-top: 3rem;
`;

const ButtonFaqWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  & button {
    margin-bottom: 0.5rem;
  }

  p,
  a {
    font-size: 0.65rem;
  }
`;

const PaymentTermsDisclosure = styled.span`
  display: flex;
  width: 100%;
  font-family: ${fontFamilies.interRegular};
  font-size: 0.75rem;
  margin: 0;
  line-height: 1rem;
  color: ${colors.darkBlue};
`;

const CompleteAppLaterWrapper = styled.div`
  padding: 1rem;
`;

const Label = styled.div`
  font-size: 1rem;
  font-family: ${fontFamilies.interSemiBold};
  color: ${colors.koalafiBlue};
  cursor: pointer;
  text-align: center;
`;

const PreApproval: React.FC<PreApprovalProps> = ({
  onNext,
  isLoading,
  orderId,
  application,
  dealer,
  authorizeCardErrors,
  role,
  onCompleteAppLater,
  track,
  page,
  identify,
  orderDetails,
  applyErrors = [],
}) => {
  const { t } = useTranslation();
  const leaseOption = selectLeaseHelper(application?.lease?.options);
  const domains = dealer.domains!;
  const hasItems = !!orderDetails?.items?.length;
  const isEcommLongForm = dealer?.channelType === ChannelTypes.Ecommerce; // does this need to make a form type check?
  const ecommApprovalOnly = getEcommApprovalOnly(dealer);

  const dtrumProps = useSessionProperies();
  const [shouldRedirect, setShouldRedirect] = useState<boolean>();
  const [isCompleteLaterRedirecting, setIsCompleteLaterRedirecting] = useState(false);

  useEffect(() => {
    page('Preapproval', { orderId, dealerId: dealer?.id, approvalAmount: leaseOption?.approvedAmount });
    track('Lease Pre-approved', { approvalAmount: leaseOption?.approvedAmount });

    dtrumProps({
      appmode: ApplicationFinancingType.lease as string,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleButtonClick = () => {
    if (!!cartValidationErr || isEmptyCartInvalid) {
      const payloadDetails = {
        orderId: orderId,
        reasonCode: ExitCodes.generalCloseModal,
        reason: 'Modal Closed - cart requires adjustment',
      } as ModalMessageFailure;

      const payload: ModalMessage = {
        wcfSuccess: false,
        closeModal: true,
        payload: payloadDetails,
      };
      modalResolver(payload, domains);
      return;
    }
    track('Pre-Approval Continue', {
      approvalAmount: leaseOption?.approvedAmount,
    });
    onNext({ type: 'NEXT' });
  };

  const handleCompleteAppLater = () => {
    track('Complete Application Later', { role });

    if (role === Role.Customer) {
      onCompleteAppLater();
    } else {
      setIsCompleteLaterRedirecting(true);
      redirectToApplicationDetails(application?.lease?.publicId);
    }
  };

  const getBottomContent = () => {
    let textContent;
    if (!!cartValidationErr && !ecommApprovalOnly) {
      textContent = (
        <CartValidationErrContent
          cartValidationErr={cartValidationErr}
          approvalAmount={financingAmount!}
          minimumSpend={minimumSpend}
          application={application}
          dealer={dealer}
          orderDetails={orderDetails}
          track={track}
          page={page}
          identify={identify}
        />
      );
    } else if (isEmptyCartInvalid || !!ecommApprovalOnly) {
      textContent = (
        <h6>
          <Trans i18nKey={($) => $.pagePreapproval.keepShopping} values={{ storeName: dealer.storeName }} components={{ strong: <strong /> }} />
        </h6>
      );
    } else {
      textContent = (
        <>
          <h6>
            <strong>{t(($) => $.pagePreapproval.finalizeApproval)}</strong>
          </h6>
          <h6 style={{ lineHeight: 1.5 }} data-testid="bodyText">
            {bodyText}
          </h6>
          <PaymentTermsDisclosure> {disclosureText}</PaymentTermsDisclosure>
        </>
      );
    }
    return (
      <BottomContent>
        {textContent}
        {!ecommApprovalOnly && (
          <ButtonFaqWrapper>
            <Button onClick={handleButtonClick} dataLoading={isLoading}>
              {buttonText}
            </Button>
            {!isEcommLongForm && (
              <CompleteAppLaterWrapper onClick={handleCompleteAppLater}>
                {isCompleteLaterRedirecting ? <DotLoader /> : <Label>{t(($) => $.pagePreapproval.completeLater)}</Label>}
              </CompleteAppLaterWrapper>
            )}
          </ButtonFaqWrapper>
        )}
      </BottomContent>
    );
  };

  const { cartValidationErr, financingAmount, minimumSpend } = useMemo(() => {
    const applyCartValidationErr = applyErrors.find((err: SubmitApplicationError) => err.__typename === 'CartValidation') as CartValidation | undefined;
    if (applyCartValidationErr) {
      let parsedMinimumSpend: number | null = null;
      if (!!applyCartValidationErr.extensions?.minimumSpend) {
        parsedMinimumSpend = parseInt(applyCartValidationErr.extensions?.minimumSpend);
      }

      return {
        cartValidationErr: applyCartValidationErr.extensions?.validationErrType,
        financingAmount: applyCartValidationErr.extensions?.approvedAmount,
        minimumSpend: parsedMinimumSpend,
      };
    } else {
      return {
        cartValidationErr: application?.lease?.cartValidationAdditionalDetails?.cartValidationType,
        financingAmount: leaseOption?.approvedAmount,
        minimumSpend: getLowestMinimumSpend(application?.lease?.options),
      };
    }
  }, [applyErrors, application?.lease, leaseOption?.approvedAmount]);

  const { bodyText, disclosureText, buttonText } = usePageText(cartValidationErr, orderDetails, application!, dealer);

  const isEmptyCartInvalid = !hasItems && dealer.channelType === ChannelTypes.Ecommerce && !dealer?.isVirtualCard;

  // hard/soft decline errors will be redirected away from the screen before this gets a chance to trigger
  if (authorizeCardErrors) {
    return (
      <ErrorsFound
        header={t(($) => $.pagePreapproval.somethingWentWrong)}
        body={t(($) => $.pagePreapproval.tryAgainOrReachOut)}
        buttonText={t(($) => $.pagePreapproval.tryAgain)}
        buttonBehavior={() => {
          setShouldRedirect(false);
        }}
        track={track}
        page={page}
        identify={identify}
      />
    );
  }

  return shouldRedirect ? (
    <LongLoader message={t(($) => $.pagePreapproval.reviewingApplication)} />
  ) : (
    <ApprovalContainer>
      <Suspense fallback={<LongLoader message={t(($) => $.pagePreapproval.reviewingApplication)} />}>
        <WallyPreApproval financingAmount={financingAmount} />
        <Gradient />
        {getBottomContent()}
      </Suspense>
    </ApprovalContainer>
  );
};

export default PreApproval;
